import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  TablePagination,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  IconButton
} from "@mui/material";
import { Search, Refresh } from "@mui/icons-material";
import { Calendar, CheckCircle2, XCircle, Clock } from "lucide-react";
import { useQuery, useQueryClient } from "react-query";
import SocialPosterModal from "../../Desiner/UpcomingEvents/components/SocialPosterModal";
import FilePreviewLightbox from "../../Desiner/UpcomingEvents/components/FilePreviewLightbox";
import InstituteLogoCell from "../../Desiner/UpcomingEvents/components/InstituteLogoCell";
import {
  fetchAdminSocialMediaUploadRequests
} from "../../../services/eventUploadsService";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return dateStr;
  }
};

const SocialPosterApproval = ({ eventTypeFilter = "Tech Camp", userRole = "admin" }) => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isSocialPosterModalOpen, setIsSocialPosterModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  // Debounce search input by 400ms
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when eventTypeFilter changes
  React.useEffect(() => {
    setPage(0);
  }, [eventTypeFilter]);

  // ── 1. Fetch Social Media Upload Requests (Server-side paginated & filtered) ──
  const {
    data: requestsResponse,
    isLoading,
    isFetching,
    refetch
  } = useQuery(
    ["admin-social-media-upload-requests", eventTypeFilter, debouncedSearch, page, rowsPerPage],
    () =>
      fetchAdminSocialMediaUploadRequests({
        eventType: eventTypeFilter,
        search: debouncedSearch,
        status: null,
        page: page + 1,
        page_size: rowsPerPage
      }),
    {
      keepPreviousData: true,
      staleTime: Infinity,
      refetchOnWindowFocus: false
    }
  );

  const filteredEvents = requestsResponse?.data || [];
  const totalCount = requestsResponse?.total ?? 0;

  const handleOpenReview = (eventItem) => {
    setSelectedEvent({
      ...eventItem,
      id: eventItem.request_id,
      request_id: eventItem.request_id,
      poster_request_id: eventItem.request_id,
      event_upload_request_id: eventItem.request_id,
      bookslot_id: eventItem.bookslot_id,
      institute_name: eventItem.institute_name,
      domain_name: eventItem.domain_name,
      event_type: eventItem.event_type,
      start_date: eventItem.start_date,
      end_date: eventItem.end_date,
      dateRange: `${formatDate(eventItem.start_date)} – ${formatDate(eventItem.end_date)}`,
      status: eventItem.status,
      poster_upload_status: eventItem.status,
      socialPosterStatus: eventItem.status,
      socialPosterFeedback: eventItem.reason || ""
    });
    setIsSocialPosterModalOpen(true);
  };

  const handleUpdateEvent = (updatedEvent) => {
    queryClient.invalidateQueries(["admin-social-media-upload-requests"]);
    refetch();
    setIsSocialPosterModalOpen(false);
    setSelectedEvent(null);
    setToast({
      open: true,
      message:
        updatedEvent.socialPosterStatus === "approved" || updatedEvent.status === "approved"
          ? "Social poster approved successfully!"
          : "Changes requested and designer notified.",
      severity:
        updatedEvent.socialPosterStatus === "approved" || updatedEvent.status === "approved"
          ? "success"
          : "info"
    });
  };

  const renderStatusBadge = (status) => {
    const raw = status || "—";
    const s = String(status || "").toLowerCase();
    const label = String(raw);

    if (s === "approved" || s === "accepted") {
      return (
        <Chip
          icon={<CheckCircle2 size={12} color="#16a34a" />}
          label={label}
          size="small"
          sx={{
            height: "22px",
            backgroundColor: "#f0fdf4",
            color: "#16a34a",
            fontWeight: 700,
            fontSize: "0.72rem",
            border: "1px solid #bbf7d0",
            borderRadius: "6px"
          }}
        />
      );
    }
    if (s === "rejected") {
      return (
        <Chip
          icon={<XCircle size={12} color="#dc2626" />}
          label={label}
          size="small"
          sx={{
            height: "22px",
            backgroundColor: "#fef2f2",
            color: "#dc2626",
            fontWeight: 700,
            fontSize: "0.72rem",
            border: "1px solid #fecaca",
            borderRadius: "6px"
          }}
        />
      );
    }
    if (s === "pending") {
      return (
        <Chip
          icon={<Clock size={12} color="#d97706" />}
          label={label}
          size="small"
          sx={{
            height: "22px",
            backgroundColor: "#fffbeb",
            color: "#d97706",
            fontWeight: 700,
            fontSize: "0.72rem",
            border: "1px solid #fde68a",
            borderRadius: "6px"
          }}
        />
      );
    }
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          height: "22px",
          backgroundColor: "#f1f5f9",
          color: "#475569",
          fontWeight: 600,
          fontSize: "0.72rem",
          border: "1px solid #cbd5e1",
          borderRadius: "6px"
        }}
      />
    );
  };

  return (
    <Box sx={{ width: "100%", fontFamily: "'Inter', sans-serif" }}>
      {/* Header & Search Bar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2.5
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.05rem" }}
          >
            Social Poster Approvals
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
            Review and approve marketing social media posters submitted by the designer team.
          </Typography>
        </Box>

        {/* Controls: Search and Refresh */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: { xs: "100%", sm: "auto" } }}>
          <TextField
            size="small"
            placeholder="Search institute or domain..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                fontSize: "0.8rem",
                height: "36px",
                width: { xs: "100%", sm: "260px" }
              }
            }}
          />

          <Tooltip title="Refresh Requests">
            <IconButton
              onClick={() => refetch()}
              disabled={isFetching}
              size="small"
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                color: "#64748b",
                "&:hover": { backgroundColor: "#f8fafc" }
              }}
            >
              <Refresh sx={{ fontSize: 18, animation: isFetching ? "spin 1s linear infinite" : "none" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Single Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          overflow: "hidden"
        }}
      >
        <Table sx={{ minWidth: 700 }}>
          <TableHead sx={{ backgroundColor: "#f8fafc" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5, width: 50 }}>
                #
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}>
                Institute Name
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5, minWidth: 90 }}>
                Edition No
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}>
                Domain Name
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}>
                Event Type
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}>
                Event Dates
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}>
                Submitted At
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}>
                Status
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5, width: 120 }}>
                Institute Logo
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5, width: 110 }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={30} sx={{ color: "#1877f2", mb: 1 }} />
                  <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.82rem" }}>
                    Loading social poster requests...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredEvents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                  <Typography variant="body2" sx={{ fontStyle: "italic", fontSize: "0.85rem" }}>
                    No social poster requests found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredEvents.map((eventItem, index) => (
                <TableRow
                  key={eventItem.request_id || `item-${index}`}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "#f8fafc" },
                    transition: "background-color 0.15s ease"
                  }}
                >
                  <TableCell sx={{ fontSize: "0.78rem", color: "#94a3b8", fontWeight: 600 }}>
                    {page * rowsPerPage + index + 1}
                  </TableCell>

                  <TableCell>
                    <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.85rem" }}>
                      {eventItem.institute_name}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ minWidth: 90 }}>
                    <Typography sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.82rem" }}>
                      {eventItem.edition_no
                        ? (String(eventItem.edition_no).startsWith("#") ? String(eventItem.edition_no) : `#${eventItem.edition_no}`)
                        : eventItem.edition
                        ? (String(eventItem.edition).startsWith("#") ? String(eventItem.edition) : `#${eventItem.edition}`)
                        : eventItem.edition_number
                        ? (String(eventItem.edition_number).startsWith("#") ? String(eventItem.edition_number) : `#${eventItem.edition_number}`)
                        : "—"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: "#334155", fontSize: "0.82rem" }}>
                      {eventItem.domain_name}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={(eventItem.event_type || "FDP").toUpperCase()}
                      sx={{
                        height: "20px",
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        backgroundColor: (eventItem.event_type || "").toLowerCase().includes("fdp") ? "#eff6ff" : "#f0fdf4",
                        color: (eventItem.event_type || "").toLowerCase().includes("fdp") ? "#1877f2" : "#16a34a",
                        border: `1px solid ${(eventItem.event_type || "").toLowerCase().includes("fdp") ? "#bfdbfe" : "#bbf7d0"}`,
                        borderRadius: "4px"
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: "0.78rem", color: "#334155" }}>
                      <Calendar size={13} color="#1877f2" />
                      <span>{formatDate(eventItem.start_date)} – {formatDate(eventItem.end_date)}</span>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.75rem", display: "block" }}>
                      {formatDate(eventItem.created_at)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    {renderStatusBadge(eventItem.status)}
                  </TableCell>

                  <TableCell align="center">
                    <InstituteLogoCell
                      logoKey={eventItem.institute_logo || eventItem.hosted_institute_logo || eventItem.logo}
                      instituteName={eventItem.institute_name}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleOpenReview(eventItem)}
                      sx={{
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: "6px",
                        py: 0.45,
                        px: 1.5,
                        color: "#ffffff",
                        backgroundColor: "#1877f2",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#1465cc",
                          boxShadow: "none"
                        }
                      }}
                    >
                      {eventItem.status === "pending" ? "Review" : "View"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: "1px solid #e2e8f0" }}
        />
      </TableContainer>

      {/* Social Poster Review & Approval Modal */}
      {isSocialPosterModalOpen && selectedEvent && (
        <SocialPosterModal
          isOpen={isSocialPosterModalOpen}
          onClose={() => {
            setIsSocialPosterModalOpen(false);
            setSelectedEvent(null);
          }}
          event={selectedEvent}
          onUpdateEvent={handleUpdateEvent}
          onPreviewFile={(file) => setPreviewFile(file)}
          userRole={userRole}
        />
      )}

      {/* Image Preview Lightbox */}
      {previewFile && (
        <FilePreviewLightbox
          previewFile={previewFile}
          onClose={() => setPreviewFile(null)}
          onRemoveFile={() => setPreviewFile(null)}
          userRole={userRole}
        />
      )}

      {/* Feedback Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%", borderRadius: "8px", fontWeight: 600, fontSize: "0.82rem" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SocialPosterApproval;
